import { useEffect, useRef } from "react";
import { Form } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dentallyLogo from "../../../assets/dentally-logo.png";
import PasswordResetInjectors from "../ForgotPassword/injectors";
import {
  selectEmail,
  selectResetPassword,
  selectVerifyOtp,
} from "../ForgotPassword/selector";
import { saveResetPassword } from "../ForgotPassword/slice";
import { stripWhitespace } from "../../../utils/formNormalizers";
import type { ResetPasswordFormValues } from "./types";
import {
  BackButton,
  FormCard,
  ImageCard,
  LeftCol,
  LogoImage,
  PageRow,
  PrimaryButton,
  RightCol,
  StyledForm,
  StyledPasswordInput,
  StyledTitle,
  SubmitFormItem,
  SubtitleText,
} from "./style";

const { Item, useForm } = Form;

const PASSWORD_RULES_MESSAGE =
  "8-20 chars, with uppercase, lowercase, number, and special char (@$#!%*?&).";
const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[#@$!%*?&])[A-Za-z\d@$#!%*?&]{8,20}$/;

export default function ResetPasswordPage() {
  const [form] = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const email = useSelector(selectEmail);
  const { verified } = useSelector(selectVerifyOtp);
  const { saving, error, success } = useSelector(selectResetPassword);
  const wasSaving = useRef(false);

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password", { replace: true });
      return;
    }
    if (!verified) {
      navigate("/verify", { replace: true });
    }
  }, [email, verified, navigate]);

  useEffect(() => {
    if (wasSaving.current && !saving && !error && success) {
      navigate("/password-changed");
    }
    wasSaving.current = saving;
  }, [saving, error, success, navigate]);

  function handleFinish(values: unknown) {
    const v = values as ResetPasswordFormValues;
    if (!email) return;
    dispatch(
      saveResetPassword({
        email,
        new_password: v.password,
        confirm_password: v.confirmPassword,
      }),
    );
  }

  return (
    <PageRow>
      <PasswordResetInjectors />
      <LeftCol xs={0} sm={0} md={0} lg={12} xl={12}>
        <ImageCard />
      </LeftCol>

      <RightCol xs={24} sm={24} md={24} lg={12} xl={12}>
        <FormCard vertical align="flex-start">
          <LogoImage
            src={dentallyLogo}
            alt="Dentally"
            width={233}
            preview={false}
          />

          <BackButton
            aria-label="Back"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/verify")}
          />

          <StyledTitle level={1}>Reset Password</StyledTitle>

          <SubtitleText>
            Your new password must not be the same as your previous password
          </SubtitleText>

          <StyledForm
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            requiredMark={false}
          >
            <Item
              name="password"
              label="New Password"
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please enter a new password" },
                { pattern: PASSWORD_REGEX, message: PASSWORD_RULES_MESSAGE },
              ]}
            >
              <StyledPasswordInput
                placeholder="Enter your new password"
                autoComplete="new-password"
              />
            </Item>

            <Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["password"]}
              normalize={stripWhitespace}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <StyledPasswordInput
                placeholder="Re-enter your new password"
                autoComplete="new-password"
              />
            </Item>

            <SubmitFormItem>
              <PrimaryButton
                type="primary"
                htmlType="submit"
                block
                loading={saving}
              >
                Update Password
              </PrimaryButton>
            </SubmitFormItem>
          </StyledForm>
        </FormCard>
      </RightCol>
    </PageRow>
  );
}
